/*
 * Copyright (c) 2026. Bluemate B.V.
 */

import Cookies from 'js-cookie';
import {getSetting} from '@dvrd/dvr-controls';

/**
 * The rebrand renamed every `bolmate*` storage key, cookie and cache to
 * `bluemate*`. Without this migration the first load after the deploy reads
 * empty values: the UI language falls back to Dutch, dismissed banners return,
 * the push schema version resets, and — worst of the set — a new device id is
 * generated, which registers the same device a second time in the push stats.
 */
const LEGACY_PREFIXES = ['bolmate-', 'bolmate_'];
const LEGACY_COOKIES = ['bolmate-device-id'];
const LEGACY_CACHE_PREFIX = 'bolmate-';

function newKey(key: string): string {
    return `blue${key.slice('bol'.length)}`;
}

function migrateStorage(storage: Storage): void {
    const legacyKeys: Array<string> = [];
    for (let i = 0; i < storage.length; i++) {
        const key = storage.key(i);
        if (key && LEGACY_PREFIXES.some(prefix => key.startsWith(prefix))) legacyKeys.push(key);
    }
    legacyKeys.forEach(key => {
        const value = storage.getItem(key);
        const target = newKey(key);
        if (value !== null && storage.getItem(target) === null) storage.setItem(target, value);
        storage.removeItem(key);
    });
}

function migrateCookies(): void {
    LEGACY_COOKIES.forEach(name => {
        const value = Cookies.get(name);
        if (value === undefined) return;
        const target = newKey(name);
        if (Cookies.get(target) === undefined) {
            const options: Cookies.CookieAttributes = {expires: 365};
            if (getSetting('mode') !== 'dev') {
                options.secure = true;
                options.sameSite = 'strict';
            }
            Cookies.set(target, value, options);
        }
        Cookies.remove(name);
    });
}

function dropLegacyCaches(): void {
    if (typeof caches === 'undefined') return;
    void caches
        .keys()
        .then(names =>
            Promise.all(names.filter(name => name.startsWith(LEGACY_CACHE_PREFIX)).map(name => caches.delete(name)))
        )
        .catch(() => undefined);
}

export function migrateLegacyBrandKeys(): void {
    try {
        migrateStorage(window.localStorage);
        migrateStorage(window.sessionStorage);
        migrateCookies();
        dropLegacyCaches();
    } catch (_err) {
        /* storage disabled — nothing to migrate */
    }
}
